import {
  Mask,
  Spinner,
  View,
  IconXLine,
  IconButton,
  Text,
  Flex,
} from '@instructure/ui';
import { useEffect, useRef, useLayoutEffect } from 'react';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';
import useDataBroker from '../../stores/dataBroker';
import useLayoutStore from '../../stores/layout';
import Checkin from '../../components/Checkin';
import RoomOptions from '../../components/Options';
import RoomSettings from '../../components/RoomSettings';
import Focus from '../../components/Focus';
import SessionGrid from '../../components/SessionGrid';
import RoomNavBar from '../../components/NavBar';
import TransferModal from '../../components/Modal/Transfer';
import EndSessionModal from '../../components/Modal/End';
import PageLoader from '../../components/Common/PageLoader';
import config from '../../config';

const VirtualTestCenter = () => {
  const { room } = useAppStore();

  const drawerWidth = useAppStore((state) => state.drawerWidth);

  const RoomContainerRef = useRef(null);
  const RoomContentRef = useRef(null);
  const DrawerLayoutRef = useRef(null);

  const { closeDrawer, drawerMode, isDrawerOpen } = useAppStore();
  const { initialStateLoaded, updateLoader, transferSessionSid } =
    useSessionStore();

  useLayoutEffect(() => {
    if (RoomContentRef) {
      useLayoutStore.getState().initialize({
        regions: {
          roomContainer: RoomContainerRef,
          roomContent: RoomContentRef,
          drawer: DrawerLayoutRef,
        },
      });
      useLayoutStore.getState().calculateLayoutSizing();
    }
  }, [RoomContainerRef]);

  useEffect(() => {
    const initializeVTC = async () => {
      // indicates where the data should be persisted, this can be either local for testing or remote...
      const dataMode = config?.vtc?.mode ?? 'remote';
      useDataBroker.getState().setDataMode(dataMode);
      const initResult = await useDataBroker.getState().initialize(dataMode)
      
      if (initResult.successful) {
        // Anything else that has to be done upon successful initialization...
        updateLoader(true);
      } else {
        //TODO: if this fails we need to show an error....
      }
    };
    initializeVTC();

    document.title = 'Virtual Test Center';
  }, []);

  const DrawerContainer = ({ children }) => (
    <View
      as="div"
      width="100%"
      height="100%"
      borderWidth="none small none none"
      borderColor="brand"
    >
      {children}
    </View>
  );

  /**
   * Returns the JSX component corresponding to the current drawer mode.
   *
   * @returns {JSX.Element|null} The component to render inside the drawer,
   *                             or null if the drawer mode is not recognized.
   */
  const getDrawerContent = () => {
    switch (drawerMode) {
      case 'checkin':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: 'Checkin', closeButton: true })}
            <Checkin />
          </DrawerContainer>
        );
      case 'focus':
        return <Focus />;
      case 'settings':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: 'Checkin', closeButton: true })}
            <RoomSettings />
          </DrawerContainer>
        );
      case 'options':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: room.roomName, closeButton: true })}
            <RoomOptions />
          </DrawerContainer>
        );
      default:
        return null;
    }
  };

  const renderDrawerHeader = (options) => {
    return (
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        padding="x-small small"
      >
        <Flex.Item shouldGrow>
          <Text size="large" color="primary">
            {options.title}
          </Text>
        </Flex.Item>
        <Flex.Item shouldGrow textAlign="end">
          {options.closeButton && (
            <IconButton
              withBackground={false}
              withBorder={false}
              size="small"
              screenReaderLabel="Unread Messages"
              margin="none"
              padding="none"
              onClick={closeDrawer}
            >
              <IconXLine />
            </IconButton>
          )}
        </Flex.Item>
      </Flex>
    );
  };

  return (
    <>
      <Flex as="div" height="100vh" width="100vw">
        <Flex.Item as="div" height={'100vh'}>
          <RoomNavBar />
        </Flex.Item>

        <Flex.Item
          as="div"
          width={isDrawerOpen ? drawerWidth : 0}
          height={'100vh'}
          background="secondary"
        >
          {getDrawerContent()}
        </Flex.Item>
        <Flex.Item
          as="div"
          id="room-content"
          elementRef={(el) => {
            RoomContentRef.current = el;
          }}
          height={'100vh'}
          background="secondary"
          shouldGrow
          shouldShrink
        >
          {!initialStateLoaded && (
            <Mask
              placement="center"
              fullscreen={true}
              themeOverride={{ zIndex: 9999 }}
            >
              <Spinner renderTitle="Loading" size="large" />
              <Text size="large" color="primary">
                Configuring Room...
              </Text>
            </Mask>
          )}
          {initialStateLoaded && <SessionGrid />}
        </Flex.Item>
      </Flex>

      {transferSessionSid && <TransferModal />}
      <EndSessionModal />
      <PageLoader />
    </>
  );
};
export default VirtualTestCenter;
